import {Component, NgZone, OnInit, QueryList, ViewChildren} from '@angular/core';
import {Toast} from './Toast';
import {ToasterService} from './toaster.service';
import {asyncScheduler, observeOn} from 'rxjs';
import {NgbAlert} from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-toaster',
  templateUrl: './toaster.component.html',
  styleUrls: ['./toaster.component.scss'],
  standalone: false
})
export class ToasterComponent implements OnInit {

  private readonly timeout = 5000;
  private autoCloseTimeoutsByToastId = new Map<string, number>();
  toasts: Toast[] = [];
  @ViewChildren(NgbAlert) ngbAlerts: QueryList<NgbAlert>;

  constructor(private toasterService: ToasterService, private zone: NgZone) {}

  ngOnInit() {
    /** When toast is caused by an error, there is a chance that the toast message will not show.
     * It seems to be because the change detection and DOM update stops working until some time or
     * a user action like a click. Or could be because of heirarchy of components as well.
     * The `asyncScheduler` and `zone.run` below together makes it so it still shows up. */
    this.toasterService.getToaster().pipe(observeOn(asyncScheduler)).subscribe((toast: Toast) => {
      this.zone.run(() => {
        this.populateDefaults(toast);

        const timeoutHandle = this.autoCloseTimeoutsByToastId.get(toast.toastId);
        if (timeoutHandle != null) {
          clearTimeout(timeoutHandle);
        }
        this.toasts = this.upsertToast(this.toasts, toast);

        // timeout <= 0 will not be automatically closed
        if (toast.timeout && isFinite(toast.timeout) && toast.timeout > 0) {
          this.autoCloseTimeoutsByToastId.set(toast.toastId, this.autoClose(toast));
        }
      });
    });

    this.toasterService.getToastIdToClose().subscribe((toastId: string) => this.closeById(toastId));
  }

  deleteToast(toast: Toast) {
    const toastIndexToClose = this.getToastIndex(this.toasts, toast.toastId);
    if (toastIndexToClose >= 0) {
      this.toasts.splice(toastIndexToClose, 1);
    }
    this.autoCloseTimeoutsByToastId.delete(toast.toastId);
  }

  private upsertToast(toasts: Toast[], toast: Toast): Toast[] {
    const toastIndex = this.getToastIndex(this.toasts, toast.toastId);
    const toastsClone = [...toasts];
    if (toastIndex >= 0) {
      toastsClone[toastIndex] = toast;
    } else {
      toastsClone.push(toast);
    }
    return toastsClone;
  }

  private getToastIndex(toasts: Toast[], toastId: string): number {
    return toasts.findIndex(toast => toast.toastId === toastId);
  }

  private closeById(toastId: string) {
    const toastIndexToClose = this.getToastIndex(this.toasts, toastId);
    /** Need to call `NgbAlert.close()` so that animation plays. Toast is then deleted by the `NgbAlert.closed` output handler `deleteToast` */
    this.ngbAlerts.get(toastIndexToClose)?.close();
  }

  private populateDefaults(toast: Toast): void {
    toast.timeout = isFinite(toast.timeout) ? toast.timeout : this.timeout;
    if (!toast.toastId) {
      toast.toastId = this.toasterService.generateToasterId();
    }
    toast.dismissible = !!toast.dismissible;
  }

  private autoClose(toast: Toast): number {
    return window.setTimeout(() => this.closeById(toast.toastId), toast.timeout);
  }
}
